import { useEffect, useState } from "react";
import { Users, Inbox, Sparkles, Star, FileText } from "lucide-react";
import { PortalShell } from "../components/layout/PortalShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { apiAdminStats, apiAdminUsers, apiAdminInquiries, apiAdminAuditPreps } from "../lib/api";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-border bg-white p-6">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <Icon className="h-5 w-5 text-brand" />
    </div>
    <div className="mt-3 font-display text-3xl font-extrabold text-navy">{value ?? "—"}</div>
  </div>
);

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [preps, setPreps] = useState([]);

  useEffect(() => {
    apiAdminStats().then(setStats).catch(() => {});
    apiAdminUsers().then(setUsers).catch(() => {});
    apiAdminInquiries().then(setInquiries).catch(() => {});
    apiAdminAuditPreps().then(setPreps).catch(() => {});
  }, []);

  return (
    <PortalShell>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy">Admin</h1>
        <p className="mt-1 text-muted-foreground">Manage users, leads and AI usage.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={Users} label="Users" value={stats.users} />
        <StatCard icon={Inbox} label="Inquiries" value={stats.inquiries} />
        <StatCard icon={FileText} label="Quotes" value={stats.quotes} />
        <StatCard icon={Star} label="AuditGuard interest" value={stats.early_access} />
        <StatCard icon={Sparkles} label="AI reports" value={
          (stats.audit_preps || 0) + (stats.gap_analyses || 0) + (stats.documents_generated || 0) +
          (stats.corrective_actions || 0) + (stats.risk_assessments || 0) + (stats.training_plans || 0) +
          (stats.safety_analyses || 0)
        } />
      </div>

      <div className="mt-8">
        <Tabs defaultValue="inquiries">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">AI Reports ({preps.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries" className="mt-4">
            <div className="overflow-x-auto rounded-2xl border border-border bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="p-4">Type</th><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Services</th><th className="p-4">AuditGuard</th><th className="p-4">Date</th></tr>
                </thead>
                <tbody>
                  {inquiries.map((q) => (
                    <tr key={q.id} className="border-b border-border/60">
                      <td className="p-4"><span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold capitalize text-brand">{q.type}</span></td>
                      <td className="p-4 font-medium text-navy">{q.name}</td>
                      <td className="p-4 text-muted-foreground">{q.email}</td>
                      <td className="p-4 text-muted-foreground">{(q.services || []).join(", ") || "—"}</td>
                      <td className="p-4">{q.auditguard_early_access ? <Star className="h-4 w-4 text-aero" /> : "—"}</td>
                      <td className="p-4 text-muted-foreground">{new Date(q.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No inquiries yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="overflow-x-auto rounded-2xl border border-border bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Provider</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-border/60">
                      <td className="p-4 font-medium text-navy">{u.name || "—"}</td>
                      <td className="p-4 text-muted-foreground">{u.email}</td>
                      <td className="p-4"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold capitalize text-steel">{u.role}</span></td>
                      <td className="p-4 capitalize text-muted-foreground">{u.auth_provider}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="ai" className="mt-4">
            <div className="space-y-3">
              {preps.map((p) => (
                <div key={p.id} className="rounded-xl border border-border bg-white p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-display text-sm font-bold text-navy">{p.input?.organization_name}</div>
                    <div className="text-xs text-muted-foreground">{p.email} · {new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{p.input?.regulatory_framework} · {p.input?.audit_type}</div>
                </div>
              ))}
              {preps.length === 0 && <div className="rounded-2xl border border-border bg-white p-8 text-center text-muted-foreground">No AI reports generated yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PortalShell>
  );
}
